import { ref, nextTick, computed, type Ref, type ComputedRef } from 'vue'
import { whatsAppToHtml } from '@/utils/whatsAppText'
import type { ProcessedMsgCp } from '@/types/chatCp'

export interface UseMessageSearchCpOptions {
  processedMsgs: ComputedRef<ProcessedMsgCp[]>
  msgsEl: Ref<HTMLElement | null>
  loadMoreMsgs: () => Promise<void>
  noMoreMsgs: Ref<boolean>
}

/**
 * Irmão WootDash-nativo de useMessageSearch.ts (que continua servindo FocoView.vue sem
 * mudança) — mesma busca textual client-side sobre mensagens já carregadas. Sem a lógica de
 * separar assinatura do agente (message_signature): o WootDash não tem esse conceito
 * hoje, então não há nada pra destacar/tirar do corpo da mensagem.
 */
export function useMessageSearchCp(options: UseMessageSearchCpOptions) {
  const { processedMsgs, msgsEl, loadMoreMsgs, noMoreMsgs } = options

  const msgSearchOpen    = ref(false)
  const msgSearchQuery   = ref('')
  const msgSearchActive  = ref('')
  const msgSearchLoading = ref(false)
  const msgCurrentIdx    = ref(0)
  const msgSearchInputEl = ref<HTMLInputElement | null>(null)

  const msgMatchIds = computed<string[]>(() => {
    if (!msgSearchActive.value) return []
    const q = msgSearchActive.value.toLowerCase()
    return processedMsgs.value.filter(m => m.body && m.body.toLowerCase().includes(q)).map(m => m.id)
  })

  function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  }

  function highlightMsgHtml(body: string | null): string {
    if (!body) return ''
    // Sem busca: a bolha mostra a formatação do WhatsApp (*negrito*, _itálico_…) como o contato vê.
    if (!msgSearchActive.value) return whatsAppToHtml(body)
    const q = msgSearchActive.value.toLowerCase()
    const result: string[] = []
    let i = 0
    while (i < body.length) {
      const j = body.toLowerCase().indexOf(q, i)
      if (j === -1) { result.push(escapeHtml(body.slice(i)).replace(/\n/g, '<br>')); break }
      if (j > i) result.push(escapeHtml(body.slice(i, j)).replace(/\n/g, '<br>'))
      result.push(`<mark class="msg-hl">${escapeHtml(body.slice(j, j + q.length))}</mark>`)
      i = j + q.length
    }
    return result.join('')
  }

  function openMsgSearch() {
    msgSearchOpen.value = true
    nextTick(() => msgSearchInputEl.value?.focus())
  }

  function closeMsgSearch() {
    msgSearchOpen.value = false
    msgSearchQuery.value = ''
    msgSearchActive.value = ''
    msgCurrentIdx.value = 0
  }

  async function doMsgSearch() {
    const q = msgSearchQuery.value.trim()
    if (!q) { msgSearchActive.value = ''; msgCurrentIdx.value = 0; return }
    msgSearchLoading.value = true
    msgSearchActive.value = q
    msgCurrentIdx.value = 0
    try {
      let iterations = 0
      while (!noMoreMsgs.value && iterations < 100) {
        await loadMoreMsgs()
        iterations++
      }
    } finally {
      msgSearchLoading.value = false
      await nextTick()
      scrollToMsgMatch()
    }
  }

  function goNextMsg() {
    if (!msgMatchIds.value.length) return
    msgCurrentIdx.value = (msgCurrentIdx.value + 1) % msgMatchIds.value.length
    nextTick(() => scrollToMsgMatch())
  }

  function goPrevMsg() {
    if (!msgMatchIds.value.length) return
    msgCurrentIdx.value = (msgCurrentIdx.value - 1 + msgMatchIds.value.length) % msgMatchIds.value.length
    nextTick(() => scrollToMsgMatch())
  }

  function scrollToMsgMatch() {
    const id = msgMatchIds.value[msgCurrentIdx.value]
    if (!id || !msgsEl.value) return
    const el = msgsEl.value.querySelector(`[data-msg-id="${id}"]`)
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }

  return {
    msgSearchOpen, msgSearchQuery, msgSearchActive, msgSearchLoading, msgCurrentIdx, msgSearchInputEl,
    msgMatchIds,
    escapeHtml, highlightMsgHtml,
    openMsgSearch, closeMsgSearch, doMsgSearch, goNextMsg, goPrevMsg, scrollToMsgMatch,
  }
}
